import { Fragment } from 'react';

import cn from 'classnames';

import AdditionalFieldsComparisonView from '../AdditianalFieldsComparisonView';
import ComparisonSectionTitle from '../ComparisonSectionTitle';
import Separator from '../../../../components/Separator';
import { Tab } from '../../../License/types/enums';
import ComparisonField from '../ComparisonField';

import useComparisonParams from '../../hooks/useComparisonParams';
import { type IComparisonField } from '../../types';

import styles from './ComparisonView.module.scss';

interface IComparisonViewProps {
  fields: IComparisonField[][];
  tab: Tab;
  values: Record<string, any>;
  pathPrefix?: string;
  showBothSides?: boolean;
}

function ComparisonView({ fields, tab, values, pathPrefix, showBothSides = true }: IComparisonViewProps) {
  const { leftEpochMillis, rightEpochMillis } = useComparisonParams();

  if (!fields.length) {
    return null;
  }

  return (
    <div className={styles.wrapper}>
      <div
        className={cn(styles.section, {
          [styles.listView as string]: tab === Tab.ITEM || tab === Tab.ATTACHED_DOCUMENT,
        })}
      >
        <ComparisonSectionTitle showStatus={tab === Tab.HEADER} isLeftVersion={true} />
        {fields.map((fieldSection, index) => (
          <Fragment key={index}>
            {index !== 0 && <Separator className="grid-full-width" />}
            {fieldSection.map(({ Component, ...field }) => {
              const RenderedComponent = Component ?? ComparisonField;

              return (
                <RenderedComponent
                  isOldVersion={Number(leftEpochMillis) < Number(rightEpochMillis)}
                  differenceKey="leftValue"
                  pathPrefix={pathPrefix}
                  key={field.fieldKey}
                  values={values}
                  {...field}
                />
              );
            })}
          </Fragment>
        ))}

        <AdditionalFieldsComparisonView
          isOldVersion={Number(leftEpochMillis) < Number(rightEpochMillis)}
          dbOptionAdditionalFields={values.dbOptionAdditionalFields}
          additionalFields={values.additionalFields}
          differenceKey="leftValue"
          entityId={values.id}
          tab={tab}
        />
      </div>

      {showBothSides && (
        <>
          <div className={styles.divider} />

          <div
            className={cn(styles.section, {
              [styles.listView as string]: tab === Tab.ITEM || tab === Tab.ATTACHED_DOCUMENT,
            })}
          >
            <ComparisonSectionTitle isLeftVersion={false} showStatus={tab === Tab.HEADER} />
            {fields.map((fieldSection, index) => (
              <Fragment key={index}>
                {index !== 0 && <Separator className="grid-full-width" />}
                {fieldSection.map(({ Component, ...field }) => {
                  const RenderedComponent = Component ?? ComparisonField;

                  return (
                    <RenderedComponent
                      isOldVersion={Number(rightEpochMillis) < Number(leftEpochMillis)}
                      differenceKey="rightValue"
                      pathPrefix={pathPrefix}
                      key={field.fieldKey}
                      values={values}
                      {...field}
                    />
                  );
                })}
              </Fragment>
            ))}
            <AdditionalFieldsComparisonView
              isOldVersion={Number(rightEpochMillis) < Number(leftEpochMillis)}
              dbOptionAdditionalFields={values.dbOptionAdditionalFields}
              additionalFields={values.additionalFields}
              differenceKey="rightValue"
              entityId={values.id}
              tab={tab}
            />
          </div>
        </>
      )}
    </div>
  );
}

export default ComparisonView;
